import type { Feature, FeatureCollection, Geometry } from "geojson";
import polygonClipping from "polygon-clipping";
import { area as turfArea } from "@turf/turf";

import { api } from "./api";

// ============================================================
// Boundary file parsing — same pipeline as the landing-page
// converter (greengrowth.tech/converter): accepts KML, KMZ and
// zipped shapefiles, analyzes placemark names / attributes, and
// lets the UI resolve field names before importing.
// ============================================================

export type TBoundaryParseErrorCode =
  | "invalidType"
  | "singleShapefileComponent"
  | "invalidZip"
  | "kmzNoKml"
  | "zipNoShapefileOrKml"
  | "missingShapefileParts"
  | "noPolygons";

export class BoundaryParseError extends Error {
  code: TBoundaryParseErrorCode;
  /** e.g. the offending extension, or the list of missing sidecar files */
  detail?: string;

  constructor(code: TBoundaryParseErrorCode, fallbackMessage: string, detail?: string) {
    super(fallbackMessage);
    this.code = code;
    this.detail = detail;
  }
}

export type TBoundaryProperty = { key: string; example: string };

export type TParsedBoundaryFile = {
  /** Polygon / MultiPolygon features in EPSG:4326, properties preserved */
  features: Feature[];
  /** true when every shape already carries a non-empty `name` */
  hasNames: boolean;
  /** up to 5 example names (when hasNames) */
  nameExamples: string[];
  /** attribute keys usable as field names, with an example value each */
  properties: TBoundaryProperty[];
};

const SHAPEFILE_COMPONENT_EXTENSIONS = [
  ".shp", ".dbf", ".shx", ".prj", ".cpg", ".qix", ".sbn", ".sbx",
];

// Properties that are file plumbing, not user data — never offered as names.
const INTERNAL_PROPERTY_KEYS = new Set([
  "name", "styleUrl", "visibility", "open", "address", "phoneNumber", "snippet",
]);

const featureName = (f: Feature): string => {
  const n = f.properties?.name;
  return typeof n === "string" ? n.trim() : n != null ? String(n).trim() : "";
};

const decodeText = (bytes: Uint8Array): string => new TextDecoder("utf-8").decode(bytes);

// OpenLayers turns every ExtendedData <Data> that carries a <displayName> into
// a {value, displayName, toString} object instead of a string, and leaks the
// last displayName as a stray `displayName` property. A <Data name="name">
// (Cropwise/FMS exports) therefore overwrites the placemark's <name> with an
// object whose toString returns undefined. Unwrap those back to plain values.
const flattenKmlDataProperties = (feature: Feature): Feature => {
  const properties: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(feature.properties ?? {})) {
    if (key === "displayName") continue;
    properties[key] =
      value != null && typeof value === "object" && "displayName" in value
        ? (value as { value?: unknown }).value ?? null
        : value;
  }
  return { ...feature, properties };
};

const kmlToFeatures = async (kmlText: string): Promise<Feature[]> => {
  const { default: KML } = await import("ol/format/KML");
  const { default: OLGeoJSON } = await import("ol/format/GeoJSON");
  const kmlFormat = new KML({ extractStyles: false });
  const geoJsonFormat = new OLGeoJSON();
  const olFeatures = kmlFormat.readFeatures(kmlText, {
    dataProjection: "EPSG:4326",
    featureProjection: "EPSG:4326",
  });
  return olFeatures.map((f) =>
    flattenKmlDataProperties(
      geoJsonFormat.writeFeatureObject(f, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:4326",
      }) as Feature
    )
  );
};

const isPolygonal = (g?: Geometry | null): boolean =>
  g != null && (g.type === "Polygon" || g.type === "MultiPolygon");

/**
 * Parse an uploaded boundary file (.kml / .kmz / zipped shapefile) into
 * polygon features plus a name/attribute analysis for the import wizard.
 */
export const parseBoundaryFile = async (file: File): Promise<TParsedBoundaryFile> => {
  const name = file.name.toLowerCase();
  const isKml = name.endsWith(".kml");
  const isZip = name.endsWith(".zip");
  const isKmz = name.endsWith(".kmz");
  const singleComponent = SHAPEFILE_COMPONENT_EXTENSIONS.find((ext) => name.endsWith(ext));

  if (singleComponent) {
    throw new BoundaryParseError(
      "singleShapefileComponent",
      `A shapefile is several files working together. Please zip the whole set (.shp, .dbf, .shx, …) and upload the .zip — not just the ${singleComponent} file.`,
      singleComponent
    );
  }
  if (!isKml && !isZip && !isKmz) {
    throw new BoundaryParseError(
      "invalidType",
      "Unsupported file type. Upload a .kml, .kmz, or a zipped shapefile (.zip)."
    );
  }

  let features: Feature[];

  if (isKml) {
    features = await kmlToFeatures(await file.text());
  } else {
    const { unzipSync } = await import("fflate");
    const buffer = await file.arrayBuffer();
    let archive: Record<string, Uint8Array>;
    try {
      archive = unzipSync(new Uint8Array(buffer));
    } catch {
      throw new BoundaryParseError("invalidZip", "Could not read the archive — the file appears to be corrupted.");
    }
    const entries = Object.keys(archive).filter((n) => !n.endsWith("/"));

    if (isKmz) {
      // KMZ = ZIP with a KML inside (Google Earth, John Deere Ops Center, …);
      // the main document is usually doc.kml but any .kml is accepted.
      const kmlEntry =
        entries.find((n) => n.toLowerCase() === "doc.kml") ||
        entries.find((n) => n.toLowerCase().endsWith(".kml"));
      if (!kmlEntry) {
        throw new BoundaryParseError("kmzNoKml", "No .kml document found inside the KMZ archive.");
      }
      features = await kmlToFeatures(decodeText(archive[kmlEntry]));
    } else {
      const extensionsInZip = new Set(
        entries.map((n) => {
          const base = (n.split("/").pop() ?? "").toLowerCase();
          const dot = base.lastIndexOf(".");
          return dot >= 0 ? base.substring(dot) : "";
        })
      );
      const hasShp = extensionsInZip.has(".shp");
      const hasDbf = extensionsInZip.has(".dbf");
      const hasShx = extensionsInZip.has(".shx");
      const kmlInZip = entries.find((n) => n.toLowerCase().endsWith(".kml"));

      if (hasShp && hasDbf && hasShx) {
        const { default: shp } = await import("shpjs");
        const geojson = await shp(buffer);
        const collections: FeatureCollection[] = Array.isArray(geojson) ? geojson : [geojson];
        features = collections.reduce<Feature[]>(
          (acc, fc) => acc.concat((fc.features ?? []) as Feature[]),
          []
        );
      } else if (hasShp || hasDbf || hasShx) {
        const missing = [
          !hasShp ? ".shp" : null,
          !hasDbf ? ".dbf" : null,
          !hasShx ? ".shx" : null,
        ].filter(Boolean) as string[];
        throw new BoundaryParseError(
          "missingShapefileParts",
          `The ZIP is missing required shapefile components: ${missing.join(", ")}. Re-export the shapefile and zip all its files together.`,
          missing.join(", ")
        );
      } else if (kmlInZip) {
        features = await kmlToFeatures(decodeText(archive[kmlInZip]));
      } else {
        throw new BoundaryParseError(
          "zipNoShapefileOrKml",
          "The ZIP contains neither a shapefile nor a KML file."
        );
      }
    }
  }

  const polygonFeatures = features.filter((f) => isPolygonal(f.geometry));
  if (polygonFeatures.length === 0) {
    throw new BoundaryParseError(
      "noPolygons",
      "No polygon boundaries found in the file. Boundaries must be Polygon or MultiPolygon shapes."
    );
  }

  // Analyze names + candidate attributes (mirrors the landing converter).
  let hasNames = true;
  const nameExamples: string[] = [];
  const propertiesMap = new Map<string, string>();
  for (const f of polygonFeatures) {
    const n = featureName(f);
    if (!n) hasNames = false;
    else if (nameExamples.length < 5) nameExamples.push(n);
    for (const [key, value] of Object.entries(f.properties ?? {})) {
      if (INTERNAL_PROPERTY_KEYS.has(key)) continue;
      if (value == null || typeof value === "object") continue;
      const text = String(value).trim();
      if (!propertiesMap.has(key)) propertiesMap.set(key, text);
      else if (!propertiesMap.get(key) && text) propertiesMap.set(key, text);
    }
  }

  return {
    features: polygonFeatures,
    hasNames,
    nameExamples,
    properties: Array.from(propertiesMap, ([key, example]) => ({ key, example })),
  };
};

// ============================================================
// Custom layers
//
// A custom layer is the same file, read for a different purpose: instead of
// asking "which attribute holds the field NAME", we ask "which attribute holds
// the NUMBER to colour by" — Tgt_Rate on a John Deere prescription, RATE or VRA
// from most agronomy tools, a lab result on a soil-sampling map.
// ============================================================

/** Attribute keys that are file plumbing or identifiers, never a map value. */
const NON_VALUE_PROPERTY_KEYS = new Set([
  "name", "styleurl", "visibility", "open", "address", "phonenumber", "snippet",
  "id", "fid", "objectid", "shape_area", "shape_leng", "shape_length",
]);

/** How many of a layer's shapes must carry a number for a column to qualify. */
const NUMERIC_COVERAGE = 0.8;

const asNumber = (raw: unknown): number | null => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (text === "") return null;
  // Tolerate a comma decimal separator — common in EU-exported prescriptions.
  const n = Number(text.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/** Read one feature's value for a chosen attribute; null when absent/unparsable. */
export const readNumericProperty = (f: Feature, key: string): number | null =>
  asNumber(f.properties?.[key]);

/**
 * Attribute keys whose values parse as numbers across most of the shapes,
 * ranked so the likeliest prescription rate column comes first.
 *
 * A stray null or an empty headland row must not disqualify a column, hence the
 * coverage threshold rather than requiring every feature to have a number.
 */
export const numericProperties = (features: Feature[]): TBoundaryProperty[] => {
  if (features.length === 0) return [];

  const counts = new Map<string, { hits: number; example: string }>();
  for (const f of features) {
    for (const [key, value] of Object.entries(f.properties ?? {})) {
      if (NON_VALUE_PROPERTY_KEYS.has(key.toLowerCase())) continue;
      const n = asNumber(value);
      if (n == null) continue;
      const entry = counts.get(key);
      if (entry) entry.hits++;
      else counts.set(key, { hits: 1, example: String(value).trim() });
    }
  }

  const needed = features.length * NUMERIC_COVERAGE;
  // Names the ag world actually uses for an application rate, best guess first.
  const preferred = ["tgt_rate", "target_rate", "rate", "vra", "rx", "seedrate", "yield"];
  const rank = (key: string) => {
    const i = preferred.indexOf(key.toLowerCase());
    return i === -1 ? preferred.length : i;
  };

  return Array.from(counts)
    .filter(([, v]) => v.hits >= needed)
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([key, v]) => ({ key, example: v.example }));
};

/** Name every feature from one of its attributes; falls back to "Field N". */
export const applyPropertyAsName = (features: Feature[], propertyKey: string): Feature[] =>
  features.map((f, i) => {
    const raw = f.properties?.[propertyKey];
    const value = raw == null ? "" : String(raw).trim();
    return {
      ...f,
      properties: { ...f.properties, name: value || `Field ${i + 1}` },
    };
  });

/** Name features sequentially: Field 1, Field 2, … */
export const applySequentialNames = (features: Feature[]): Feature[] =>
  features.map((f, i) => ({
    ...f,
    properties: { ...f.properties, name: `Field ${i + 1}` },
  }));

export type TBoundaryImportResult = {
  /** Fields actually created. */
  imported: number;
  /** Boundaries the account already had — skipped, not failed. */
  duplicates: number;
  /** Boundaries that failed for any other reason. */
  failed: number;
  /** First real failure message, for the error toast. */
  firstError?: string;
};

/** The backend's reason code for "you already have this exact boundary". */
const DUPLICATE_CODE = "DUPLICATE_FIELD_GEOMETRY";

const isDuplicateError = (e: unknown): boolean =>
  (e as { errorCode?: string })?.errorCode === DUPLICATE_CODE;

/** Overlap at or above which two outlines are the same field. Matches the
 *  server's DUPLICATE_BOUNDARY_OVERLAP — both sides must agree or the client
 *  would post something the server then refuses. */
const SAME_FIELD_OVERLAP = 0.98;
/** Area gap beyond which two outlines cannot overlap by SAME_FIELD_OVERLAP. */
const AREA_TOLERANCE = 0.02;

const asMulti = (g: Geometry): number[][][][] =>
  (g.type === "MultiPolygon" ? g.coordinates : [(g as any).coordinates]) as number[][][][];

/** Intersection-over-union of two outlines; 0 when they don't overlap. */
const overlapRatio = (a: Geometry, b: Geometry): number => {
  const ha = (coords: unknown[]): number =>
    coords.length === 0
      ? 0
      : turfArea({ type: "MultiPolygon", coordinates: coords } as any) / 10_000;
  try {
    const A = asMulti(a) as any;
    const B = asMulti(b) as any;
    const inter = ha(polygonClipping.intersection(A, B));
    if (inter === 0) return 0;
    const union = ha(polygonClipping.union(A, B));
    return union > 0 ? inter / union : 0;
  } catch {
    return 0;
  }
};

/**
 * Drop features that are the same field as one earlier in the same file.
 *
 * Two reasons this belongs on the client, not only on the server:
 *
 *  - A file that lists the same field in two folders/layers would otherwise
 *    post it twice and get a 409 back for a field that did not exist a second
 *    ago — confusing, and it inflates the "already on the map" count.
 *  - The requests go out in parallel, so for near-identical (not byte-identical)
 *    outlines both copies can pass the server's check before either has been
 *    written. The exact-geometry unique index cannot catch that pair. Removing
 *    them here is what makes the parallel import safe.
 *
 * The match is by overlap, not by bytes: one production upload carried the same
 * field at 7 and at 24 vertices — 23.4006 ha beside 23.4108 ha, 99.95% the same
 * ground. Comparison is O(n²) in the worst case but each test is gated on an
 * area window, so a real file does a few hundred clips, not tens of thousands.
 */
const withoutRepeats = (features: Feature[]): { unique: Feature[]; repeats: number } => {
  const seen = new Set<string>();
  const kept: Array<{ f: Feature; ha: number }> = [];
  let repeats = 0;

  for (const f of features) {
    // parseBoundaryFile only ever yields polygons; a shape with no geometry is
    // neither importable nor a duplicate, so it is dropped without counting.
    if (!f.geometry) continue;
    const key = JSON.stringify(f.geometry);
    if (seen.has(key)) {
      repeats++; // identical bytes — no need to measure
      continue;
    }
    seen.add(key);

    let ha = 0;
    try {
      ha = turfArea(f as any) / 10_000;
    } catch {
      ha = 0;
    }

    let duplicate = false;
    for (const prev of kept) {
      if (ha > 0 && prev.ha > 0) {
        const ratio = ha / prev.ha;
        if (ratio < 1 - AREA_TOLERANCE || ratio > 1 + AREA_TOLERANCE) continue;
      }
      if (overlapRatio(f.geometry, prev.f.geometry!) >= SAME_FIELD_OVERLAP) {
        duplicate = true;
        break;
      }
    }
    if (duplicate) repeats++;
    else kept.push({ f, ha });
  }

  return { unique: kept.map((k) => k.f), repeats };
};

/**
 * POST each boundary as a new field.
 *
 * Duplicates are an expected outcome, not a failure: the server refuses a
 * boundary the account already holds (409 DUPLICATE_FIELD_GEOMETRY), which is
 * exactly what re-uploading last season's file should do. They are counted and
 * reported, never toasted one-by-one — a re-imported 380-field file used to be
 * capable of 380 error toasts. Requests stay parallel; `allSettled` keeps one
 * refusal from discarding the fields that did import.
 */
export const importBoundaryFeatures = async (
  features: Feature[],
  seasonId?: string,
  cropId?: string,
  farmId?: string
): Promise<TBoundaryImportResult> => {
  const { unique, repeats } = withoutRepeats(features);

  const results = await Promise.allSettled(
    unique.map((f, i) =>
      api.post(
        "/api/v1/fields",
        {
          name: featureName(f) || `Field ${i + 1}`,
          geometry: f.geometry,
          seasonId,
          ...(cropId ? { cropId } : {}),
          ...(farmId ? { farmId } : {}),
        },
        // Failures are tallied and reported once by the caller.
        { silent: true }
      )
    )
  );

  let imported = 0;
  let duplicates = repeats;
  let failed = 0;
  let firstError: string | undefined;
  for (const r of results) {
    if (r.status === "fulfilled") imported++;
    else if (isDuplicateError(r.reason)) duplicates++;
    else {
      failed++;
      firstError ??= (r.reason as Error)?.message;
    }
  }
  return { imported, duplicates, failed, firstError };
};
